import {
  MISSION_STATUS_ENUM,
  MISSION_DISPLAY_STATUS_READY_TO_PUBLISH,
} from '../../shared/constants'

/**
 * Translates what DataFetchingComponent hands `fetchData` (filter labels,
 * search text, `LABEL_ASC`-style sort, first/skip) into the arguments of
 * `cityPlan.missionsPage`. The single place the table's vocabulary meets
 * the API's, so it is tested rather than sprinkled through components.
 */

/** Table filter labels. */
export const FILTER_STATUS = 'Status'
export const FILTER_WEEK = 'Week'
export const FILTER_BA = 'BA'

/** Values of the BA filter. */
export const BA_ASSIGNED = 'assigned'
export const BA_UNASSIGNED = 'unassigned'

/** Table header sort keys -> API order by. */
export const ORDER_BY_MAP = {
  LABEL_ASC: 'LABEL_ASC',
  LABEL_DESC: 'LABEL_DESC',
  DATE_ASC: 'DATETIME_ASC',
  DATE_DESC: 'DATETIME_DESC',
  WEEK_ASC: 'DATETIME_ASC',
  WEEK_DESC: 'DATETIME_DESC',
  STATUS_ASC: 'STATUS_ASC',
  STATUS_DESC: 'STATUS_DESC',
  BA_ASC: 'USER_ASC',
  BA_DESC: 'USER_DESC',
}

export const DEFAULT_ORDER_BY = 'DATETIME_ASC'

/**
 * The status filter's options: the real statuses plus the READY_TO_PUBLISH
 * display status, which is a DRAFT with the flag set.
 * @returns {Array<{label: string, value: string}>}
 */
export const statusFilterOptions = () =>
  [
    MISSION_STATUS_ENUM.DRAFT,
    MISSION_DISPLAY_STATUS_READY_TO_PUBLISH,
    MISSION_STATUS_ENUM.AVAILABLE,
    MISSION_STATUS_ENUM.BOOKED,
    MISSION_STATUS_ENUM.CHECKED_IN,
    MISSION_STATUS_ENUM.UNDER_REVIEW,
    MISSION_STATUS_ENUM.COMPLETED,
    MISSION_STATUS_ENUM.PAID,
    MISSION_STATUS_ENUM.CANCELLED,
  ].map(status => ({ label: status, value: status }))

/**
 * The API filter for one status filter value.
 * @param {?string} status
 * @returns {object}
 */
export const statusToApiFilter = status => {
  if (!status) return {}
  if (status === MISSION_DISPLAY_STATUS_READY_TO_PUBLISH)
    return { statuses: [MISSION_STATUS_ENUM.DRAFT], isReadyToPublish: true }
  if (status === MISSION_STATUS_ENUM.DRAFT)
    return { statuses: [MISSION_STATUS_ENUM.DRAFT], isReadyToPublish: false }
  return { statuses: [status] }
}

/**
 * Builds the `missionsPage` filter from the table's state.
 * @param {object} args
 * @param {?string} [args.Status] Selected status filter value
 * @param {?string} [args.Week] Selected week key
 * @param {?string} [args.BA] BA_ASSIGNED / BA_UNASSIGNED
 * @param {?string} [args.filter] Search text
 * @param {?string} [args.isoWeek] A fixed week (roster page), wins over Week
 * @returns {object} CityPlanMissionFilterInput
 */
export const buildMissionsFilter = ({
  Status,
  Week,
  BA,
  filter,
  isoWeek,
} = {}) => {
  const apiFilter = { ...statusToApiFilter(Status) }
  const week = isoWeek || Week
  if (week) apiFilter.isoWeeks = [String(week)]
  if (BA === BA_ASSIGNED) apiFilter.hasUser = true
  if (BA === BA_UNASSIGNED) apiFilter.hasUser = false
  const search = (filter || '').trim()
  if (search) apiFilter.search = search
  return apiFilter
}

/**
 * Builds the whole `missionsPage` argument set.
 * @param {object} args What DataFetchingComponent passes to fetchData
 * @param {object} [options]
 * @param {?string} [options.isoWeek] Fixed week for the roster page
 * @returns {{first: number, skip: number, filter: object, orderBy: string}}
 */
export const buildMissionsQueryArgs = (args = {}, { isoWeek } = {}) => ({
  first: Number(args.first) || 50,
  skip: Number(args.skip) || 0,
  filter: buildMissionsFilter({ ...args, isoWeek }),
  orderBy: ORDER_BY_MAP[args.orderBy] || DEFAULT_ORDER_BY,
})
